import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useRoutes } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import LoginPage from './pages/LoginPage';
import SimpleLayout from './layouts/simple';
import DashboardLayout from './layouts/dashboard';
import logger from './utils/logger';
import UserPage from './pages/UserPage';
import Page404 from './pages/Page404';
import Alert from './pages/alert/index';
import Report from './pages/report';
import Activated from './pages/activated';
import DashboardAppPage from './pages/DashboardAppPage';
import Information from './pages/information';
import Actions from './pages/action';
import ActivityLog from './pages/activitylogs';
import AddUser from './pages/add/adduser';
import EditUser from './pages/user/edit';
import EditAction from './pages/action/edit';
import AddAction from './pages/action/add';
import SignUp from './pages/SignUp';
import Calendar from './pages/calendar';
import Summary from './pages/summary';
import Approval from './pages/approval';
import DetailAlert from './pages/alert/detail-alert';
import Data from './pages/dataset';
import Reset from './pages/Reset';
import LoadingPage from './pages/loadingpage';
import ProtectedRoute from './utils/protectedroute';
import { getFirstAccessibleMenuPath, getAcl, getMenuAccess, getToken } from './utils/common';

// Component to redirect based on permissions (used for both root and dashboard index)
function PermissionBasedRedirect({ defaultPath = '/dashboard/app' }) {
  const [redirectPath, setRedirectPath] = React.useState(null);

  React.useEffect(() => {
    const redirectToFirstMenu = async () => {
      // Check if user is authenticated
      const token = getToken();
      if (!token || token === 'undefined' || token === 'gada') {
        // User not authenticated, redirect to login
        setRedirectPath('/login');
        return;
      }

      // Get ACL
      let acl = getAcl();
      const isValidAcl = (aclValue) => {
        if (!aclValue) return false;
        if (aclValue === '[]' || aclValue === 'null' || aclValue === 'undefined' || aclValue === '') return false;
        try {
          const parsed = JSON.parse(aclValue);
          return Array.isArray(parsed) && parsed.length > 0;
        } catch {
          return false;
        }
      };

      // Wait for ACL if needed
      if (!isValidAcl(acl)) {
        try {
          await getMenuAccess(true);
          await new Promise((resolve) => setTimeout(resolve, 500));
          acl = getAcl();
        } catch (error) {
          logger.error('PermissionBasedRedirect: Failed to fetch permissions:', error);
          setRedirectPath(defaultPath);
          return;
        }
      }

      if (isValidAcl(acl)) {
        const firstMenuPath = getFirstAccessibleMenuPath(acl);
        setRedirectPath(firstMenuPath);
      } else {
        setRedirectPath(defaultPath);
      }
    };

    redirectToFirstMenu();
  }, [defaultPath]);

  // Show a loading indicator instead of null to ensure FCP can be detected
  if (!redirectPath) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          width: '100%',
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return <Navigate to={redirectPath} replace />;
}

PermissionBasedRedirect.propTypes = {
  defaultPath: PropTypes.string,
};

export default function Router() {
  const routes = useRoutes([
    {
      path: '/login',
      element: <LoginPage />,
    },

    {
      path: '/dashboard',
      element: (
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <PermissionBasedRedirect defaultPath="/dashboard/app" />,
        },
        { path: 'app', element: <DashboardAppPage /> },
        { path: 'user', element: <UserPage /> },
        { path: 'alert', element: <Alert /> },
        { path: 'report', element: <Report /> },
        { path: 'dataset', element: <Data /> },
        { path: 'information', element: <Information /> },
        { path: 'action', element: <Actions /> },
        { path: 'activitylogs', element: <ActivityLog /> },
        { path: 'adduser', element: <AddUser /> },
        { path: 'action/add', element: <AddAction /> },
        { path: 'user/edit', element: <EditUser /> },
        { path: 'action/edit', element: <EditAction /> },
        { path: 'calendar', element: <Calendar /> },
        { path: 'summary', element: <Summary /> },
        { path: 'about', element: <Approval /> },
        { path: 'alert/detail-alert', element: <DetailAlert /> },
      ],
    },

    {
      element: <SimpleLayout />,
      children: [
        { path: '404', element: <Page404 /> },
        { path: 'activated', element: <Activated /> },
        { path: 'signup', element: <SignUp /> },
        { path: 'password/reset', element: <Reset /> },
        { path: 'welcome', element: <LoadingPage /> },
      ],
    },

    // Default redirects - redirect to first accessible menu based on permissions
    {
      path: '/',
      element: <PermissionBasedRedirect defaultPath="/dashboard/app" />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
